import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnInit, viewChild } from '@angular/core';
import { LocalStorageService } from '@app/auth/local-storage.service';
import { GeneralLookupService } from '@app/cem/core-data/services/general-lookup.service';
import { GeneralProjectService } from '@app/cem/project/services/general-project.service';
import { HandleDates } from '@app/common/services/handle-dates.service';
import { PlexEnvironment } from '@app/env';
import { PxdAgGridColumnTypes, PxdAgGridDefaultColumnDef, PxdAgGridExcelStyles, PxdAgGridFilterParams, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions, PXDDefaultExcelExportParams } from '@app/shared/aggrid/pxd-ag-grid-options';
import { ProjectTypeId } from '@app/shared/data/project-enums';
import { WellTankBatteryProjectDto } from '@app/shared/models/well-tank-battery-project';
import { SplitButtonComponent } from '@progress/kendo-angular-buttons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { AgGridAngular } from 'ag-grid-angular';
import { GridReadyEvent } from 'ag-grid-community';
import { AppStorage } from 'app/common/services/data-api/app-storage.service';

@Component({
  selector: 'app-wells',
  templateUrl: './wells.component.html',
  styleUrls: ['./wells.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    LoaderComponent,
    SplitButtonComponent,
  ],
})
export class WellsComponent implements OnInit {
  private http = inject(HttpClient);
  private generalLookupService = inject(GeneralLookupService);
  private projectService = inject(GeneralProjectService);
  private localStorage = inject(LocalStorageService);
  private store = inject(AppStorage);
  private handleDates = inject(HandleDates);
  public environment = inject(PlexEnvironment);

  private readonly agDataGrid = viewChild<AgGridAngular>('agGridFF');
  readonly removeButton = viewChild<SplitButtonComponent>('removeButton');

  public selectedWells: any[] = [];
  public loading: boolean = false;
  public showWarning: boolean = false;

  public gridOptions: PxdAgGridOptions = {
    cellSelection: true,
    rowGroupPanelShow: 'always',
    rowSelection: { mode: 'multiRow', headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false },
    defaultColDef: PxdAgGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    excelStyles: PxdAgGridExcelStyles,
    defaultExcelExportParams: PXDDefaultExcelExportParams,
    columnDefs: [
      { field: 'Ssn', headerName: 'SSN', type: ['textColumn'], editable: false },
      { field: 'WellName', headerName: 'Well Name', editable: false },
      {
        field: 'SpudDate',
        headerName: 'Spud Date',
        type: ['dateColumn'],
        comparator: this.dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: PxdAgGridFilterParams.customDate,
        editable: false
      },
      {
        field: 'MaxPadRigReleaseDate',
        headerName: 'Pad Rig Release Date(MAX)',
        type: ['dateColumn'],
        comparator: this.dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: PxdAgGridFilterParams.customDate,
        editable: false
      },
      {
        field: 'MinPopDate',
        headerName: 'Pad POP',
        type: ['dateColumn'],
        comparator: this.dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: PxdAgGridFilterParams.customDate,
        editable: false
      },
      { field: 'PPID', headerName: 'PPID', editable: false },
      { field: 'DevGroupId', headerName: 'Dev Group ID', editable: false },
      { field: 'ActualDrillGroupReference', headerName: 'Drill Group Id', headerTooltip: 'ActualDrillGroupReference', editable: false },
      { field: 'AreaTeamArea', headerName: 'Area Team Area', editable: false },
      { field: 'SdeFacilityIdOverride', headerName: 'Tank Battery Facility ID', editable: false },
      { field: 'TankBatteryAssetDescription', headerName: 'Tank Battery Asset', editable: false },
      // TODO: to be resumed when EDIT feature comes. 
      // {
      //   field: 'SdeFacilityIdOverride', headerName: 'Facility Status',
      //   cellRenderer: params => {
      //       return this.getTankBattery(params.value);
      //   },
      //   cellEditorSelector: params => {
      //       return {
      //           component: 'pxdAgGridSelectEditor',
      //           params: {
      //               values: Array.from(this.TankBatteries.values()).map(d => {
      //                   return { value: d.TankBatteryId, text: d.Name };
      //               })
      //           }
      //       };
      //   },
      //   editable: (params) => {
      //     return params.data.InitialAndExpansionProjectIds == null;
      //   }
      // },
      { field: 'Operator', headerName: 'Operator', editable: false },
      { field: 'InitialOrExpansionProjectId', headerName: 'TB Project ID', editable: false },
      { field: 'TBProjectSubType', headerName: 'TB Project Sub Type', editable: false },
      { field: 'TBProjectName', headerName: 'TB Project Name', editable: false },
      { field: 'TBProjectStatus', headerName: 'TB Project Status', editable: false },
      { field: 'TBProjectRRSpanDays', headerName: 'TB Project RR Span (Days)', editable: false },
      { field: 'OtherTBProjectIds', headerName: 'Other TB Project Ids', editable: false },
      {
        field: 'FlowlineProjectId',
        headerName: 'Flowline Project ID',
        editable: false,
        cellRenderer: params => {
          const value = params.value ? params.value : '';
          return `<span title="${value}">${value}</span>`;
        }
      },
      {
        field: 'WellPadFacilityProjectId',
        headerName: 'Well Pad Facility Project ID',
        editable: false,
        cellRenderer: params => {
          const value = params.value ? params.value : '';
          return `<span title="${value}">${value}</span>`;
        }
      },
      {
        field: 'WellHookupProjectId',
        headerName: 'Well Hookup Project ID',
        editable: false,
        cellRenderer: params => {
          const value = params.value ? params.value : '';
          return `<span title="${value}">${value}</span>`;
        }
      },
      {
        field: 'PadConstructionProjectId',
        headerName: 'Pad Construction Project ID',
        editable: false,
        cellRenderer: params => {
          const value = params.value ? params.value : '';
          return `<span title="${value}">${value}</span>`;
        }
      }
    ]
  }
  public rowData: WellTankBatteryProjectDto[];
  TankBatteries: Map<number, any> = new Map();
  public editedAssets: Map<number, any> = new Map<number, any>();
  private BASE_URL = this.environment.apiUrl;
  private scenarioId;
  private assetTeamId;
  public createButtonData: Array<any> = [
    {
      key: '129',
      text: 'Initial Build',
      click: () => { this.createTBProject('129'); },
      disabled: false
    },
    {
      key: '130',
      text: 'Expansion',
      click: () => { this.createTBProject('130'); },
      disabled: false
    },
    {
      key: '131',
      text: 'Compressor',
      click: () => { this.createTBProject('131'); },
      disabled: false
    }];
  public removeButtonData: Array<any> = [
    {
      key: ProjectTypeId.TankBattery,
      text: 'Tank Battery',
      click: () => { this.removeTBProject(ProjectTypeId.TankBattery); },
      disabled: false
    },
    {
      key: ProjectTypeId.Flowline,
      text: 'Flowline',
      click: () => { this.removeTBProject(ProjectTypeId.Flowline); },
      disabled: false
    },
    {
      key: ProjectTypeId.WellPadFacility,
      text: 'Well Pad Facility',
      click: () => { this.removeTBProject(ProjectTypeId.WellPadFacility); },
      disabled: false
    },
    {
      key: ProjectTypeId.WellHookup,
      text: 'Well Hookup',
      click: () => { this.removeTBProject(ProjectTypeId.WellHookup); },
      disabled: false
    },
    {
      key: ProjectTypeId.PadConstruction,
      text: 'Pad Construction',
      click: () => { this.removeTBProject(ProjectTypeId.PadConstruction); },
      disabled: false
    }];

  ngOnInit(): void {
    this.store.knownEvents.scenarioSelected.subscribe(async t => {
      if (t) {
        this.scenarioId = t.ScenarioId;
        this.assetTeamId = t.AssetTeamId;
      }
    });
    this.store.knownEvents.assetTeamSelected.subscribe(async t => {
      if (t) {
        this.assetTeamId = t.AssetTeamId;
        await this.refreshData();
      }
    });
  }

  getTankBattery = (id) => {
    if (!id) return '';
    let tb = this.TankBatteries.get(id);
    if (tb) return tb.Name;
    return '';
  }

  async onGridReady(e: GridReadyEvent) {
    this.generalLookupService.get("TankBatteries", false).then(async (resp) => {
      this.TankBatteries = new Map(resp.value.map(a => [a.TankBatteryId, a]));
      await this.refreshData();
    });
  }

  private formatSelectionQueryString(): string {
    let selectFilter = '&$select=Ssn';
    return `TankBatteryProjectAssetMappingView?$filter=(AssetTeamId eq ${this.assetTeamId} and ScenarioId eq ${this.scenarioId} and AssetAllocated eq false) ${selectFilter}`;
  }

  public async getWellTankBatteryProjectDtos(): Promise<any> {
    let url = `${this.BASE_URL}welltankbatteryproject/alltankbatteryprojects`;
    return await this.http.get<WellTankBatteryProjectDto[]>(url).toPromise().then(x => this.handleDates.handleDates(x));   //Handle dates doesnt like comma delimited project ids
  }

  createTBProject(key: string) {
    this.IsLoadingAction(true);
    if (key === '131') {
      this.navigateToProjects(key);
    }
    else {
      this.generalLookupService.getFiltered(this.formatSelectionQueryString())
        .then(payload => {
          if (this.selectedWells.map(x => x.Ssn).some(r => payload.value.includes(r))) {
            this.IsLoadingAction(false);
            this.showWarning = true;
          }
          else {
            this.navigateToProjects(key);
          }
        });
    }
  }

  removeTBProject(projectTypeId: number) {
    this.loading = true;
    this.removeButton().toggle(false);
    this.projectService.unAssignProjectAssets(this.selectedWells.map(x => x.AssetId), projectTypeId)
      .subscribe(async result => {
        this.selectedWells = [];
        this.rowData = await this.getWellTankBatteryProjectDtos();
        this.agDataGrid().api.setGridOption('rowData', this.rowData);
        this.loading = false;
      });
  }

  navigateToProjects(key: string) {
    this.localStorage.setItem('tb-selected-wells', { key: key, wells: this.selectedWells });
    window.open('/projects/home', '_blank');
    this.IsLoadingAction(false);
    this.showWarning = false;
  }

  async refreshData() {
    this.IsLoadingAction(true);
    this.rowData = await this.getWellTankBatteryProjectDtos();
    this.agDataGrid().api.setGridOption('rowData', this.rowData);
    this.IsLoadingAction(false);
  }

  onRowDataChanged(e: any) {
    if (e.columnApi) {
      e.columnApi.autoSizeAllColumns();
    }
  }

  onSelectedRowAdded(params) {
    this.selectedWells = [];
    this.createButtonData[0].disabled = false;
    this.createButtonData[1].disabled = false;
    this.agDataGrid().api.getSelectedRows().map((x) => this.selectedWells.push(x));
    if (this.selectedWells.some(x => x.InitialOrExpansionProjectId !== null)) {
      this.createButtonData[0].disabled = true;
      this.createButtonData[1].disabled = true;
    }
  }

  IsLoadingAction(action: boolean) {
    this.loading = action;
    this.createButtonData[0].disabled = action;
    this.createButtonData[1].disabled = action;
    this.createButtonData[2].disabled = action;
    if (this.selectedWells.some(x => x.InitialOrExpansionProjectId !== null)) {
      this.createButtonData[0].disabled = true;
      this.createButtonData[1].disabled = true;
    }
  }

  dateComparator(date1: any, date2: any) {
    const date1Value = new Date(date1);
    const date2Value = new Date(date2);

    if (date1Value < date2Value) {
      return -1;
    } else if (date1Value > date2Value) {
      return 1;
    } else {
      return 0;
    }
  }
}